import { Framework, IHistoryItem } from '@/framework';
import { getReferenceLinesState } from '../states/reference-lines-state';
import {
    IHistoryAddData,
    IHistoryClearData,
    IHistoryDeleteData,
    IHistoryLockData,
    IHistoryShowData,
    IHistoryUpdateData,
    ReferenceLineHistoryType,
    type ReferenceLine,
} from '../types';

export class ReferenceLineHistory extends Framework {
    constructor(private id: string) {
        super(id);

        this.registerAddLine();
        this.registerClearLine();
        this.registerDeleteLine();
        this.registerLockLine();
        this.registerShowLine();
        this.registerUpdateLine();
    }

    private registerAddLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.ADD, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryAddData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { item } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).addReferenceLine(item);
                }
                if (isUndo) {
                    getReferenceLinesState(id).deleteReferenceLine(item.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryAddData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryAddData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onMessage: (message: [ReferenceLine], isRedo, isUndo, isWrite) => {
                const id = this.id;
                const item = message[0];
                if (isRedo || isWrite) {
                    getReferenceLinesState(id).addReferenceLine(item);
                    getReferenceLinesState(id).setShowReferenceLines(true);
                }
                if (isUndo) {
                    getReferenceLinesState(id).deleteReferenceLine(item.id);
                }
            },
        });
    }

    private registerClearLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.CLEAR, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryClearData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { item } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).setReferenceLines([]);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setReferenceLines(item);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryClearData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryClearData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onMessage: (message: [ReferenceLine[]], isRedo, isUndo, isWrite) => {
                const id = this.id;
                const item = message[0];
                if (isRedo || isWrite) {
                    getReferenceLinesState(id).setReferenceLines([]);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setReferenceLines(item);
                }
            },
        });
    }

    private registerDeleteLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.DELETE, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryDeleteData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { item } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).deleteReferenceLine(item.id);
                }
                if (isUndo) {
                    getReferenceLinesState(id).addReferenceLine(item);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryDeleteData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryDeleteData>) => {
                return {
                    message: [historyItem.data.data.item],
                };
            },
            onMessage: (message: [ReferenceLine], isRedo, isUndo, isWrite) => {
                const id = this.id;
                const item = message[0];
                if (isRedo || isWrite) {
                    getReferenceLinesState(id).deleteReferenceLine(item.id);
                }
                if (isUndo) {
                    getReferenceLinesState(id).addReferenceLine(item);
                }
            },
        });
    }

    private registerLockLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.LOCK, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryLockData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { lock } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).setLockReferenceLines(lock);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setLockReferenceLines(!lock);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryLockData>) => {
                return {
                    message: [historyItem.data.data.lock],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryLockData>) => {
                return {
                    message: [historyItem.data.data.lock],
                };
            },
            onMessage: (message: [boolean], isRedo, isUndo, isWrite) => {
                const id = this.id;
                const lock = message[0];
                if (isRedo || isWrite) {
                    getReferenceLinesState(id).setLockReferenceLines(lock);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setLockReferenceLines(!lock);
                }
            },
        });
    }

    private registerShowLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.SHOW, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryShowData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { show } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).setShowReferenceLines(!show);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setShowReferenceLines(show);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryShowData>) => {
                return {
                    message: [historyItem.data.data.show],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryShowData>) => {
                return {
                    message: [historyItem.data.data.show],
                };
            },
            onMessage: (message: [boolean], isRedo, isUndo, isWrite) => {
                const id = this.id;
                const show = message[0];
                if (isRedo || isWrite) {
                    getReferenceLinesState(id).setShowReferenceLines(!show);
                }
                if (isUndo) {
                    getReferenceLinesState(id).setShowReferenceLines(show);
                }
            },
        });
    }

    private registerUpdateLine() {
        this.framework.service.history.register(ReferenceLineHistoryType.UPDATE, {
            owner: 'plugin/user-editor/reference-lines',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryUpdateData>, isRedo: boolean, isUndo: boolean) => {
                const id = this.id;
                const { oldInfo, newInfo } = historyItem.data.data;
                if (isRedo) {
                    getReferenceLinesState(id).updateReferenceLine(newInfo);
                }
                if (isUndo) {
                    getReferenceLinesState(id).updateReferenceLine(oldInfo);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryUpdateData>) => {
                return {
                    message: [historyItem.data.data.newInfo],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryUpdateData>, isRedo) => {
                return {
                    message: [isRedo ? historyItem.data.data.newInfo : historyItem.data.data.oldInfo],
                };
            },
            onMessage: (message: [ReferenceLine]) => {
                const id = this.id;
                const item = message[0];
                getReferenceLinesState(id).updateReferenceLine(item);
            },
        });
    }
}
